/* eslint-disable import/no-restricted-paths */
import { chillnnContentsPageFactory, type StudioImageMast } from "@CHILLNN-Inc/chillnn-abr-studio";
import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState, type ComponentProps } from "react";

import { getURL } from "@/lib/aws-amplify/storage";
import type { ChillnnStudioDriver } from "@/lib/chillnn-studio-driver";
import type { S3Object, Snack as Snack_GraphQL, SnackTemplate } from "@/lib/gql/__generated__/graphql";
import { SaleTypeForPlan } from "@/lib/gql/__generated__/graphql";

import { s3Repository } from "@/components/features/snack/_shared/repository/image";
import { imageMastInteractor } from "@/components/features/snack/_shared/repository/image-mast";

import type {
	Snack,
	SnackOwnerType,
	SnackS3Object,
	SnackTemplateCategoryMast,
	SnackTemplateMast,
	SnackType,
} from "@CHILLNN-Inc/chillnn-abr-snack/dist/entity/type";
import type {
	ChillnnContentsPage,
	IChillnnTemplateCategoryModel,
	IChillnnTemplateModelRepository,
	IChillnnTemplatePageModel,
	ImageMast as ImageMast_ChillnnABRStudio,
	OtherResource,
} from "@CHILLNN-Inc/chillnn-abr-studio";

export type UseCreateEditorBaseModelProps = {
	hotelID: string;

	targetSnackItem:
		| {
				isNew: true;
				uuid: string;
		  }
		| { isNew: false; snack: S3Object; uuid: string };

	planList: {
		plans: { inSale: boolean; isPrivate: boolean; planID: string; mainKeyVisual: S3Object; name: string }[];
	};
	roomList: {
		rooms: {
			inSale: boolean;
			saleTypeForPlan: SaleTypeForPlan;
			roomID: string;
			mainKeyVisual: S3Object;
			name: string;
		}[];
	};
	snackListParentTemplate: { snacks: { uuid: string; thumbnail: string; title: string; snack: S3Object }[] };
	snackListSystemTemplate: { snacks: Snack_GraphQL[] };
	snackTemplateSystem: SnackTemplate;
};

type ChillnnStudioDriverProps = ComponentProps<typeof ChillnnStudioDriver>;

export const useCreateEditorBaseModel = ({
	hotelID,

	targetSnackItem,
	planList,
	roomList,
	snackListParentTemplate,
	snackListSystemTemplate,
	snackTemplateSystem,
}: UseCreateEditorBaseModelProps): Pick<
	ChillnnStudioDriverProps["editorProps"],
	| "page"
	| "hotelPlanResources"
	| "hotelRoomResources"
	| "parentTemplates"
	| "snackTemplates"
	| "imageRepository"
	| "imageDeleter"
> => {
	const { isNew } = targetSnackItem;

	/**
	 * DOMをJSONで表現したものであり、ChillnnStudioEditorはこのJSONデータを編集するためのエディタです。
	 * このデータはMutableです。
	 * callbackSaveが呼ばれた場合、このデータ自体をS3にアップロードしS3のエイリアスをGraphQLのAPI経由で保存してください。
	 * SnackやHPBの本体として保存されているS3Objectを取得することで、このデータを得ることができます。
	 */
	const [page, setPage] = useState<ChillnnContentsPage | null>(null);
	useEffect(() => {
		if (isNew) {
			setPage(chillnnContentsPageFactory.factory({ default: "", en: null }, [], {}));
		} else {
			const { snack } = targetSnackItem;
			const { bucket, key, region } = targetSnackItem.snack;

			s3Repository
				.fetchObject<ChillnnContentsPage>({
					bucket,
					key,
					region,
					url: getURL(snack),
				})
				.then(setPage);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	// const page = isNew
	// 	? chillnnContentsPageFactory.factory({ default: "", en: null }, [], {})
	// 	: s3Repository.fetchObject<ChillnnContentsPage>({
	// 			bucket: targetSnackItem.snack.bucket,
	// 			key: targetSnackItem.snack.key,
	// 			region: targetSnackItem.snack.region,
	// 			url: getURL(targetSnackItem.snack),
	// 		});

	/**
	 * 予約コンポーネントを開く際に指定できるリソースを設定します。
	 */
	const hotelPlanResources: OtherResource[] = useMemo(() => {
		return planList.plans
			.filter((plan) => plan.inSale || (plan.inSale && plan.isPrivate))
			.map((plan) => {
				return {
					uuid: plan.planID,
					thumbnail: getURL(plan.mainKeyVisual),
					title: plan.name,
				} satisfies OtherResource;
			});
	}, [planList]);

	/**
	 * 予約コンポーネントを開く際に指定できるリソースを設定します。
	 */
	const hotelRoomResources: OtherResource[] = useMemo(() => {
		return roomList.rooms
			.filter((room) => room.inSale && room.saleTypeForPlan === SaleTypeForPlan.Ordinary)
			.map((room) => {
				return {
					uuid: room.roomID,
					thumbnail: getURL(room.mainKeyVisual),
					title: room.name,
				} satisfies OtherResource;
			});
	}, [roomList]);

	/**
	 * companyIDに紐づくテンプレートを流し込みます。
	 * SnackでもHPBでも同様のGraphQLのAPIを使ってください。 Templateとして登録したCompanyに紐づくデータを取得してください。
	 * 以下のプロパティ名でカテゴリを作成してください
	 * uuid: "commonpage" title: "共通テンプレート"
	 */
	const parentTemplates = useMemo(
		() =>
			(function () {
				const parentTemplates = snackListParentTemplate.snacks;
				// const parentTemplates = snackListParentTemplate.snacks.sort((a, b) => compareNumDesc(a.updatedAt, b.updatedAt));
				const chillnnParentTemplateModels: IChillnnTemplatePageModel[] = parentTemplates.map((snack) => {
					const url = getURL(snack.snack);

					return {
						uuid: snack.uuid,
						thumbnail: snack.thumbnail,
						title: snack.title,
						fetchContent: async () =>
							await s3Repository.fetchObject({
								bucket: snack.snack.bucket,
								key: snack.snack.key,
								region: snack.snack.region,
								url,
							} satisfies SnackS3Object),
					};
				});
				const chillnnParentTemplateCategory: IChillnnTemplateCategoryModel = {
					uuid: "commonpage",
					title: "共通テンプレート",
					fetchTemplates: chillnnParentTemplateModels,
				};
				return chillnnParentTemplateCategory;
			})(),
		[snackListParentTemplate.snacks],
	);

	/**
	 * snackTemplates
	 */
	const snackTemplates: IChillnnTemplateModelRepository = useMemo(
		() =>
			(function () {
				const snacks: Snack[] = snackListSystemTemplate.snacks
					// .sort((a, b) => compareNumDesc(a.updatedAt, b.updatedAt))
					.map((item) => {
						/**
						 * S3Objectをstringに変換して保存している（旧仕様を引き継いでいる）
						 * 例
						 * {
						 *		bucket: "chillnn-studio",
						 *		key: "public/2021-09-01/1630510000000.json",
						 *		region: "ap-northeast-1",
						 *		mimeType: "application/json",
						 *		fileName: "1630510000000.json",
						 * }
						 * ↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓
						 * `{"bucket":"chillnn-studio","key":"public/2021-09-01/1630510000000.json","region":"ap-northeast-1","mimeType":"application/json","fileName":"1630510000000.json"}`
						 */
						const stringifiedSnack = JSON.stringify(item.snack);

						const snack: Snack = {
							uuid: item.uuid,
							snackType: item.snackType as unknown as SnackType,
							ownerID: item.hotelID,
							ownerType: item.ownerType as unknown as SnackOwnerType,
							isPublished: item.isPublished,
							isParentTemplate: item.isParentTemplate,
							slug: item.slug,
							title: item.title,
							description: item.description,
							thumbnail: item.thumbnail,
							snack: stringifiedSnack,
							availablePlansForHotel: item.availablePlansForHotel,
							createdAt: item.createdAt,
							updatedAt: item.updatedAt,
						} satisfies Snack;

						return snack;
					});

				const snackTemplate: SnackTemplateMast = {
					ownerID: snackTemplateSystem.hotelID,
					uuid: snackTemplateSystem.uuid,
					templateCategoryMasts: snackTemplateSystem.templateCategoryMasts.map((item) => {
						return {
							uuid: item.uuid,
							title: { default: item.title },
							snackUUIDs: item.snackUUIDs,
						} satisfies SnackTemplateCategoryMast;
					}),
					createdAt: snackTemplateSystem.createdAt,
					updatedAt: snackTemplateSystem.updatedAt,
					deletedAt: snackTemplateSystem.deletedAt,
				} satisfies SnackTemplateMast;

				const templateModels: IChillnnTemplatePageModel[] = snacks.map((item) => {
					const s3Object = JSON.parse(item.snack) as S3Object;
					const url = getURL(s3Object);

					return {
						uuid: item.uuid,
						thumbnail: item.thumbnail,
						title: item.title,
						fetchContent: async () =>
							await s3Repository.fetchObject({
								bucket: s3Object.bucket,
								key: s3Object.key,
								region: s3Object.region,
								url,
							} satisfies SnackS3Object),
					};
				});

				const fetchTemplateCategories: IChillnnTemplateCategoryModel[] =
					snackTemplate.templateCategoryMasts.map((item) => {
						return {
							uuid: item.uuid,
							title: item.title.default,
							fetchTemplates: templateModels.filter((model) => item.snackUUIDs.includes(model.uuid)),
						} satisfies IChillnnTemplateCategoryModel;
					});

				return {
					fetchTemplateCategories: fetchTemplateCategories,
				};
			})(),
		[snackListSystemTemplate.snacks, snackTemplateSystem],
	);

	const apolloClient = useApolloClient();

	/**
	 * 施設に登録されている画像を取得するための関数 SnackでもHPBでも同様のAPIを使ってください。
	 */
	const imageRepository = useCallback<ChillnnStudioDriverProps["editorProps"]["imageRepository"]>(async () => {
		const imageMasts = await imageMastInteractor(apolloClient).fetchMast(hotelID);

		return imageMasts.map((imageMast) => {
			return {
				...imageMast,
				src: getURL(imageMast.image),
			} satisfies StudioImageMast;
		});
	}, [apolloClient, hotelID]);

	/**
	 * 画像を削除するための関数です。 GraphQLのAPI経由で画像のエイリアスを削除してください。 画像のエイリアスは削除されますが、画像自体は削除されません。
	 */
	const imageDeleter = useCallback<ChillnnStudioDriverProps["editorProps"]["imageDeleter"]>(
		async function (uuid: string): Promise<ImageMast_ChillnnABRStudio> {
			const imageMast = await imageMastInteractor(apolloClient).deleteImage(hotelID, uuid);
			return imageMast as ImageMast_ChillnnABRStudio;
		},
		[apolloClient, hotelID],
	);

	return {
		page,

		imageRepository,
		imageDeleter,

		hotelPlanResources,
		hotelRoomResources,

		parentTemplates,
		snackTemplates,
	};
};
